import { SettingsService } from 'src/app/services/settings/settings.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { CurrentUiStoreService } from '../ui/current-ui-store.service';
import { AsgardFeatureNames } from 'src/models/AsgardFeatures';
import { CurrentUIState } from 'src/models/UIState';
import { SpellCheckWordMgrService } from './spell-check-word-mgr.service';
import { isSymbolSpaceOrEnter, isSymbolSpace } from 'src/app/core/common';
import { CorrectionListService, ICorrectionListEvent } from '../correction-list/correction-list.service';
import { SpellCheckWordStatus } from 'src/app/core/html-and-text-utils';
import { SpellCheckStorageService } from './spell-check-storage';
import { ISettings } from 'src/app/core/interfaces';
import { SpellCheckDialogService } from './spell-check-dialog.service';

export interface ISpellCheckResponse {
  word: string;
  position: number;
  correct: boolean;
  suggestions: string[];
  languageCode: string;
}

@Injectable({
  providedIn: 'root'
})
export class SpellCheckService {
  private active: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private isLocked: boolean = false;
  private previousSpellCheckOnSetting: boolean | null = null;

  constructor(
    private uiStore: CurrentUiStoreService,
    private readonly wrdMgrService: SpellCheckWordMgrService,
    private readonly splchkDlgService: SpellCheckDialogService,
    private readonly correctionListService: CorrectionListService,
    private readonly spellCheckStorageService: SpellCheckStorageService,
    private readonly settingsService: SettingsService,
  ) {
    this.initSubscirptions();
  }

  init = (e: KeyboardEvent) => {

    if (this.active.value) {
      if (isSymbolSpaceOrEnter(e.code)) {
        this.isSpaceFired(e);
        this.wrdMgrService.initSpellCheck();
      }
    }
  }

  runSpellCheck = () => {
    if (this.active.value) {
      this.wrdMgrService.initSpellCheck();
    }
  }

  onKeyDownSpellCheck = (e: KeyboardEvent) => {
    if (this.active.value) {
      if (isSymbolSpaceOrEnter(e.code)) { // enter or space typed
        if (this.wrdMgrService.inMisspeledNode()) {
          e.preventDefault();
          this.splchkDlgService.hideDialog();
        }

      }
      this.wrdMgrService.hideDialogIfShowing();
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {  // left or right arrow pressed
        if (this.splchkDlgService.dialogActive && !this.wrdMgrService.inMisspeledNode()) {
          this.splchkDlgService.hideDialog();
        } else if (!this.splchkDlgService.dialogActive && this.wrdMgrService.inMisspeledNode()) {
          this.openDialog();
        }
      } else if (e.key.length === 1 || e.key === 'Backspace' || e.key === 'Delete') { // any printable symbol added/removed
        if (this.wrdMgrService.checkCurrentWordSpell()) {
          this.splchkDlgService.hideDialog();
        }
      } else {
        this.splchkDlgService.hideDialog();
      }
    }

  onIncorrectWordClickedHandler = () => {
    if (this.active.value && !this.wrdMgrService.checkCurrentWordSpell()) {
      this.openDialog();
    } else {
      this.splchkDlgService.hideDialog();
    }
  }

  toggleSpellCheck = () => {
    if (this.active.value) {
     this.turnOff();
    } else {
      this.turnOn();
    }
  }

  getActiveObjObserverable(): Observable<boolean> {
    return this.active.asObservable();
  }

  getProcessingObservable(): Observable<boolean> {
    return this.wrdMgrService.getProcessingObservable();
  }

  private openDialog(): void {
    const pos = this.wrdMgrService.getMisspeledWordPos();
    const suggestions = this.wrdMgrService.getCurrentWordSuggestions();

    if (pos === null) {
      return;
    }

    this.splchkDlgService.drawTheDialog(pos, suggestions || []);
  }

  private turnOn(): void {
    this.active.next(true);
    this.uiStore.setItemActive(AsgardFeatureNames.SPELL_CHECK);
    this.runSpellCheck();
  }

  private turnOff(): void {
    this.wrdMgrService.unmarkAllMisspelledWords();
    this.splchkDlgService.hideDialog();
    this.active.next(false);
    this.uiStore.setItemUnActive(AsgardFeatureNames.SPELL_CHECK);
  }

  private isSpaceFired(e: KeyboardEvent): boolean {
    if (isSymbolSpace(e.code)) {
      const node = this.wrdMgrService.inMisspeledNode();
      if (node) {
        this.wrdMgrService.placeCaretAfterNode(node);
      }
    }
    return false;
  }

  private initSubscirptions(): void {
    this.uiStore.getUIState().subscribe(
      (i: CurrentUIState) => {
        this.active.next(i[AsgardFeatureNames.SPELL_CHECK].active);
        this.isLocked = i[AsgardFeatureNames.SPELL_CHECK].locked;
      }
    );

    this.correctionListService.wordAdded.subscribe((e: ICorrectionListEvent) => {
      this.spellCheckStorageService.addWordToStorage(
        e.langCode, {word: e.word, correct: true,
        status: SpellCheckWordStatus.VALID, suggestions: []}
      );

      this.runSpellCheck();
    });

    this.correctionListService.wordDeleted.subscribe((e: ICorrectionListEvent) => {
      this.spellCheckStorageService.deleteWordFromStorage(e.langCode, e.word);
      this.runSpellCheck();
    });

    this.settingsService.getSettingsObservable().subscribe((s: ISettings) => {
      if (s) {
        if (s.spellCheckOn === this.previousSpellCheckOnSetting) {
          return;
        }

        this.previousSpellCheckOnSetting = s.spellCheckOn;
        if (!this.isLocked) {
          if (s.spellCheckOn) {
            this.turnOn();
          } else {
            this.turnOff();
          }
        }
      }
    });
  }

}
